import {useQuery} from '@tanstack/react-query';

import apiService from '../../transport/apiService';
import {RewardsGetAccountResponse} from '../../transport/types';

export const rewardsKeys = {
  all: ['rewards'] as const,
  account: (phoneNumber: string) => ['rewards', 'account', phoneNumber] as const,
};

/**
 * Fetches rewards account data for a logged-in user.
 *
 * Note: apiService.rewardsGetAccount swallows errors internally (try/catch + logError),
 * returning null on failure. We treat null as an error by throwing, which triggers
 * TanStack's retry (2x exponential backoff) and eventually sets isError: true.
 *
 * Sentry reporting happens inside apiService.rewardsGetAccount via logError on first failure.
 * No duplicate reporting needed here.
 */
export const useRewardsAccount = (phoneNumber: string | undefined) => {
  return useQuery<RewardsGetAccountResponse>({
    queryKey: rewardsKeys.account(phoneNumber || ''),

    queryFn: async () => {
      const result = await apiService.rewardsGetAccount({
        phoneNumber: phoneNumber!,
      });

      if (result?.status === 200 && result.data) {
        return result.data;
      }

      // apiService swallows errors and returns null — treat as failure
      // so TanStack retries and eventually sets isError: true
      throw new Error('Failed to load rewards account');
    },

    enabled: !!phoneNumber,
    staleTime: 2 * 60 * 1000, // 2 min — card data changes infrequently
  });
};
